import type { ObjectionCode } from './objection-codes';

/**
 * What each objection code IS, with the customer's typical words.
 *
 * One text for two readers: the analysis prompt (`objection_handling`
 * guideline, prompt-builder) and the classifier that maps historical free
 * labels onto codes (`apps/ops-tools/src/scripts/voc-label-map`). Two copies
 * would let the arms drift apart, and the label map would then count history
 * in codes that mean something slightly different from the live ones.
 *
 * Keyed by `ObjectionCode`, not `Record<string, string>`: a code added to the
 * set without a definition must fail to compile, not render `undefined` into
 * a paid prompt. Boundaries are drawn by positive example on purpose: a
 * prohibition-only rule generalizes into silence, and here silence means
 * `other`. The text is part of the cache-stable system prompt — editing it
 * moves the prompt-size ratchet in prompt-builder.
 */
export const OBJECTION_CODE_DEFINITIONS: Readonly<Record<ObjectionCode, string>> = {
  price:
    'the amount itself — "too expensive", "cheaper elsewhere", asks for a discount or promo code',
  budget:
    'no money available or allocated right now — "not in the budget", "no funds this season"',
  financing: 'needs or doubts a loan, leasing or other external financing',
  payment_terms:
    'how and when to pay — deferral, instalments, prepayment size, payment method',
  no_need:
    'does not need it at all, or not interested — "we already have one", "not relevant"',
  timing:
    'needs it, but NOT NOW — "later", "after the season", "next quarter", postponed decision. Needing it urgently is delivery_availability',
  delivery_availability:
    'delivery time or cost, stock availability, logistics, lead times, urgency — "need it before the harvest"',
  product_fit:
    'doubts it matches the need — size, compatibility, specification, configuration, assortment; does not want the offered brand, model or a used unit',
  quality_reliability:
    'doubts quality, durability, warranty coverage or the technical condition of the item',
  trust_service:
    'doubts the company, its service, competence or responsiveness — not the product itself',
  competitor_alternative:
    'prefers, already uses or is comparing a specific alternative supplier or offer',
  decision_authority:
    'cannot decide alone — must consult a partner, manager, owner, accountant or family',
  indecision:
    'hesitates WITHOUT naming a reason — "I will think about it". If a reason is named, use that reason\'s code instead',
  documents_contract:
    'contract clauses, invoices, taxes, certificates or other paperwork',
  upsell_declined:
    'declines an ADDITIONAL product or service offered on top of the main one',
  returns_compensation:
    'demands or disputes a return, refund, exchange, replacement instead of repair, or compensation',
  responsibility_dispute:
    'disputes who is responsible — for a defect, installation or assembly, transport, delay or repair',
  unwanted_contact: 'objects to being contacted at all — "stop calling me"',
  other: 'a real objection that fits none of the above',
};
